import React, { useState, useRef, useEffect, useMemo } from "react";
import { css } from "@emotion/css";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { Block } from "../../../../types/sprintLessonTypes";

// Feedback Modal component
export interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedbackBlock?: Block;
  onContinue: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  feedbackBlock,
  onContinue,
}) => {
  const [contentOverflow, setContentOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Check if content needs more space and adjust modal height
  useEffect(() => {
    if (contentRef.current && isOpen) {
      const { scrollHeight, clientHeight } = contentRef.current;
      setContentOverflow(scrollHeight > clientHeight * 1.1); // Add 10% buffer
    }
  }, [isOpen, feedbackBlock]);

  // Modal animation states
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Set visible immediately without delay
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isVisible && isOpen) {
      onClose();
    }
  };

  // Early return if not open
  if (!isOpen) return null;

  // We'll show a loading state if feedbackBlock is not available yet

  const isComplete = feedbackBlock?.is_block_complete === true;

  // Define loading animation styles
  const loadingContainerStyle = css`
    margin-top: 1rem;
    padding: 2rem;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0.5rem;
    border-left: 4px solid #4a9eff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `;

  const loadingPulseStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1.5rem 0;
  `;

  const pulseCircleStyle = css`
    height: 12px;
    width: 12px;
    border-radius: 50%;
    background-color: #4a9eff;
    margin: 0 6px;
    display: inline-block;
    animation: pulse 1.5s infinite ease-in-out;

    @keyframes pulse {
      0%,
      100% {
        transform: scale(0.75);
        opacity: 0.5;
      }
      50% {
        transform: scale(1);
        opacity: 1;
      }
    }
  `;

  // Prepare feedback content
  const feedbackContent = (() => {
    // Show loading animation if feedbackBlock is null or has no content
    if (!feedbackBlock || !feedbackBlock.content) {
      return (
        <div className={loadingContainerStyle}>
          <h4>Analyzing your response...</h4>
          <div className={loadingPulseStyle}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={css`
                  ${pulseCircleStyle}
                  animation-delay: ${i * 0.2}s;
                `}
              />
            ))}
          </div>
        </div>
      );
    }

    // If we wanted, we could have different implementations for different
    // block types / feedback types/structures
    if (feedbackBlock?.content && "feedback" in feedbackBlock.content) {
      return (
        <div
          css={css`
            margin-top: 1rem;
            padding: 1rem;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 0.5rem;
          `}
        >
          <h4
            css={css`
              margin-top: 0;
            `}
          >
            Feedback
          </h4>
          <p>{feedbackBlock.content.feedback}</p>
        </div>
      );
    }

    // Generic fallback for other block types
    return (
      <div
        css={css`
          margin-top: 1rem;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 0.5rem;
          border-left: 4px solid #4a9eff;
          white-space: pre-wrap;
        `}
      >
        <h4
          css={css`
            margin-top: 0;
          `}
        >
          Feedback
        </h4>
        <pre
          css={css`
            overflow-x: auto;
            font-family: monospace;
            font-size: 0.9rem;
          `}
        >
          {JSON.stringify(feedbackBlock.content, null, 2)}
        </pre>
      </div>
    );
  })();

  // Modal overlay and container styles
  const overlayStyle = css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, ${isVisible ? "0.5" : "0"});
    z-index: 1000;
    transition: background-color 0.1s ease;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  `;

  const modalContainerStyle = css`
    position: relative;
    width: 100%;
    max-width: 800px;
    height: ${contentOverflow ? "95%" : "70%"};
    background-color: #1a1a2a;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 1.5rem;
    transform: translateY(${isVisible ? "0" : "100%"});
    transition: transform 0.1s ease;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
  `;

  const modalContentStyle = css`
    flex: 1;
    overflow-y: auto;
    margin-bottom: 1rem;
    padding-right: 0.5rem;

    /* Custom scrollbar for webkit browsers */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #2a2a3a;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #4a4a5a;
      border-radius: 3px;
    }
  `;

  const buttonContainerStyle = css`
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  `;

  const buttonBaseStyle = css`
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.2s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const continueButtonStyle = css`
    ${buttonBaseStyle}
    background-color: ${isComplete ? "#4f46e5" : "#3a3a4a"};
    border: none;
    color: white;

    &:hover {
      background-color: ${isComplete ? "#4338ca" : "#3a3a4a"};
      transform: ${isComplete ? "translateY(-1px)" : "none"};
    }

    &:active {
      transform: ${isComplete ? "translateY(0)" : "none"};
    }
  `;

  // Pulse animation for loading state
  const pulseAnimation = css`
    @keyframes pulse {
      0% {
        opacity: 0.6;
      }
      50% {
        opacity: 0.9;
      }
      100% {
        opacity: 0.6;
      }
    }
    animation: pulse 1.5s infinite ease-in-out;
  `;

  // Style for close button
  const closeButtonStyle = css`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: transparent;
    border: none;
    color: #a0a0a0;
    font-size: 1.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    transition: all 0.2s ease;

    &:hover {
      color: white;
      background: rgba(255, 255, 255, 0.1);
    }
  `;

  return createPortal(
    <div className={overlayStyle} onTransitionEnd={handleTransitionEnd}>
      <div className={modalContainerStyle} onClick={(e) => e.stopPropagation()}>
        <button
          className={closeButtonStyle}
          onClick={onClose}
          aria-label="Close feedback modal"
        >
          ✕
        </button>

        <div className={modalContentStyle} ref={contentRef}>
          <h3>Response Feedback</h3>
          {feedbackContent}
        </div>

        <div className={buttonContainerStyle}>
          <button
            className={continueButtonStyle}
            onClick={isComplete ? onContinue : undefined}
            disabled={!isComplete}
          >
            {isComplete ? (
              "Continue"
            ) : (
              <>
                <span className={pulseAnimation}>Processing</span>
                <span
                  css={css`
                    display: inline-flex;
                    margin-left: 0.5rem;
                  `}
                >
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      css={css`
                        height: 4px;
                        width: 4px;
                        border-radius: 50%;
                        background-color: white;
                        margin: 0 2px;
                        display: inline-block;
                        animation: pulse 1.5s infinite ease-in-out;
                        animation-delay: ${i * 0.2}s;
                      `}
                    />
                  ))}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Pull-up strap component
export interface PullUpStrapProps {
  feedbackAvailable: boolean;
  feedbackModalOpen: boolean;
  setFeedbackModalOpen: (open: boolean) => void;
}

// MOBILE_NAV_HEIGHT from NavBar.tsx is 60px
const MOBILE_NAV_HEIGHT = "60px";

export const PullUpStrap: React.FC<PullUpStrapProps> = ({
  feedbackAvailable,
  feedbackModalOpen,
  setFeedbackModalOpen,
}) => {
  const pullUpStrapStyle = css`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #1a1a2a;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    padding: 0.5rem 1.5rem;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 990;
    transition: transform 0.3s ease, opacity 0.3s ease;
    width: 100%;
    height: 40px;
    opacity: ${feedbackAvailable && !feedbackModalOpen ? 1 : 0};
    pointer-events: ${feedbackAvailable && !feedbackModalOpen
      ? "auto"
      : "none"};
    transform: ${feedbackAvailable && !feedbackModalOpen
      ? "translateY(0)"
      : "translateY(100%)"};

    @media (max-width: 768px) {
      /* Adjust position to avoid overlap with mobile navbar */
      bottom: ${MOBILE_NAV_HEIGHT};
    }
  `;

  return (
    <div
      className={pullUpStrapStyle}
      onClick={() => feedbackAvailable && setFeedbackModalOpen(true)}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        <span
          css={css`
            display: block;
            width: 40px;
            height: 4px;
            background-color: #6b7280;
            border-radius: 2px;
            margin-bottom: 8px;
          `}
        />
        <FontAwesomeIcon
          icon={faChevronUp}
          css={css`
            font-size: 18px;
            color: #ffffff;
            opacity: 0.8;
          `}
        />
      </div>
    </div>
  );
};
